import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { Calendar3 } from 'react-bootstrap-icons';
import {
  Alert,
  Button,
  Col,
  Form as BsForm,
  InputGroup,
  Row,
  Spinner,
} from 'react-bootstrap';
import { format } from 'date-fns';
import RequiredAsteriskComponent from '../../components/Generic/RequiredAsteriskComponent';
import DailyHoursListView from '../../components/WorkerTimesheet/DailyHoursListView';
import ExpensesSection from '../../components/WorkerTimesheet/ExpensesSection';
import AttachmentsSection from '../../components/WorkerTimesheet/AttachmentsSection';
import {
  buildDailyHoursForMonth,
  sumDayHours,
  toPayloadDailyHours,
} from '../../components/WorkerTimesheet/dailyHoursUtils';
import {
  generateWorkerTimesheetFrontendId,
  submitWorkerTimesheet,
} from '../../clients/worker_timesheet_client';
import { showToast } from '../../config_and_helpers/helpers';
import { logInfo } from '../../config_and_helpers/logger';
import { emailRegex, MinyanLogo } from '../../config_and_helpers/config';
import '../../components/WorkerTimesheet/WorkerTimesheet.css';

const LOG_CONTEXT = 'WorkerTimesheetPage';

/** Seconds to show success confirmation before remounting an empty form. */
const SUCCESS_REDIRECT_SECONDS = 10;

/**
 * Soft client-side cap per upload (MB); server default is typically 15 MB.
 * @type {number}
 */
export const WORKER_TIMESHEET_MAX_UPLOAD_MB = 15;

const MAX_UPLOAD_BYTES = WORKER_TIMESHEET_MAX_UPLOAD_MB * 1024 * 1024;

/**
 * Yup schema for worker identity, month, daily hours, and expense rows.
 * File sizes are checked on submit (see {@link collectFileSizeErrors}).
 */
const validationSchema = Yup.object({
  first_name: Yup.string().trim().required('שדה שם פרטי הוא חובה'),
  last_name: Yup.string().trim().required('שדה שם משפחה הוא חובה'),
  worker_reference_id: Yup.string()
    .trim()
    .required('שדה מספר תעודת זהות הוא חובה')
    .max(
      128,
      'מספר תעודת זהות יכול להכיל עד 128 תווים',
    ),
  month: Yup.string()
    .required('שדה חודש הוא חובה')
    .matches(/^\d{4}-(0[1-9]|1[0-2])$/, 'פורמט נדרש: YYYY-MM'),
  submitter_email: Yup.string()
    .trim()
    .test(
      'valid-email',
      'יש להזין כתובת אימייל תקינה',
      value => !value || emailRegex.test(value),
    ),
  notes: Yup.string(),
  daily_hours: Yup.array()
    .of(
      Yup.object({
        work_date: Yup.string().required(),
        regular_hours: Yup.mixed().test(
          'nonneg',
          'חייב להיות ≥ 0',
          v => hourNonNegative(v),
        ),
        daycare_hours: Yup.mixed().test(
          'nonneg',
          'חייב להיות ≥ 0',
          v => hourNonNegative(v),
        ),
        meeting_hours: Yup.mixed().test(
          'nonneg',
          'חייב להיות ≥ 0',
          v => hourNonNegative(v),
        ),
        training_hours: Yup.mixed().test(
          'nonneg',
          'חייב להיות ≥ 0',
          v => hourNonNegative(v),
        ),
        variable_hours: Yup.mixed().test(
          'nonneg',
          'חייב להיות ≥ 0',
          v => hourNonNegative(v),
        ),
        transport_hours: Yup.mixed().test(
          'nonneg',
          'חייב להיות ≥ 0',
          v => hourNonNegative(v),
        ),
        is_sick_day: Yup.boolean(),
        is_off_day: Yup.boolean(),
        sick_note_frontend_id: Yup.string(),
        sick_note_file: Yup.mixed().nullable(),
        sick_note_filename: Yup.string(),
        sick_note_content_type: Yup.string(),
        sick_note_size: Yup.number(),
        _rowTouched: Yup.boolean(),
      }),
    )
    .test('unique-dates', 'קיימים תאריכי עבודה כפולים בטבלה', arr => {
      if (!arr?.length) return true;
      const dates = arr.map(r => r.work_date);
      return new Set(dates).size === dates.length;
    })
    .test(
      'in-month',
      'כל התאריכים חייבים להיות בחודש שנבחר',
      function (arr) {
        const month = this.parent.month;
        if (!month || !arr?.length) return true;
        return arr.every(r => r.work_date && r.work_date.startsWith(month));
      },
    )
    .test(
      'sick-note-required',
      'יש לצרף אישור רופא/מרפאה עבור יום מחלה',
      arr => {
        if (!arr?.length) return true;
        return arr.every(r => !r?.is_sick_day || r?.sick_note_file instanceof File);
      },
    )
    .test(
      'day-status-mutual-exclusion',
      'לא ניתן לסמן גם יום מחלה וגם יום חופשה/חופש באותו תאריך',
      arr => {
        if (!arr?.length) return true;
        return arr.every(r => !(r?.is_sick_day && r?.is_off_day));
      },
    ),
  expenses: Yup.array().of(
    Yup.object({
      frontend_id: Yup.string(),
      description: Yup.string()
        .trim()
        .required('שדה תיאור הוא חובה')
        .max(512, 'מקסימום 512 תווים'),
      amount: Yup.number()
        .transform((v, o) => (o === '' || o === null ? undefined : v))
        .typeError('יש להזין מספר תקין')
        .required('שדה סכום הוא חובה')
        .moreThan(0, 'הסכום חייב להיות גדול מ-0'),
      receipt: Yup.mixed().nullable(),
    }),
  ),
  attachments: Yup.array().of(
    Yup.object({
      frontend_id: Yup.string(),
      file: Yup.mixed().nullable(),
      caption: Yup.string(),
    }),
  ),
});

/**
 * @param {*} v
 * @returns {boolean}
 */
function hourNonNegative(v) {
  if (v === '' || v == null) return true;
  const n = typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'));
  return Number.isFinite(n) && n >= 0;
}

/**
 * Collects human-readable file size violation messages.
 *
 * @param {{ receipt?: File|null }[]} expenses
 * @param {{ is_sick_day?: boolean, sick_note_file?: File|null }[]} dailyHours
 * @param {{ file?: File|null }[]} attachments
 * @param {number} maxBytes
 * @returns {string[]}
 */
function collectFileSizeErrors(expenses, dailyHours, attachments, maxBytes) {
  const messages = [];
  (expenses || []).forEach((e, i) => {
    if (e.receipt instanceof File && e.receipt.size > maxBytes) {
      messages.push(
        `קבלה בהוצאה בשורה ${i + 1} חורגת מ-~${WORKER_TIMESHEET_MAX_UPLOAD_MB} MB (גבול רך).`,
      );
    }
  });
  (dailyHours || []).forEach((row, i) => {
    if (
      row?.is_sick_day &&
      row.sick_note_file instanceof File &&
      row.sick_note_file.size > maxBytes
    ) {
      messages.push(
        `אישור רופא/מרפאה עבור יום ${i + 1} חורג מ-~${WORKER_TIMESHEET_MAX_UPLOAD_MB} MB (גבול רך).`,
      );
    }
  });
  (attachments || []).forEach((a, i) => {
    if (a.file instanceof File && a.file.size > maxBytes) {
      messages.push(
        `קובץ מצורף ${i + 1} חורג מ-~${WORKER_TIMESHEET_MAX_UPLOAD_MB} MB (גבול רך).`,
      );
    }
  });
  return messages;
}

/**
 * Public worker monthly timesheet form (no authentication).
 *
 * @component
 * @returns {JSX.Element}
 */
const WorkerTimesheetPage = () => {
  const defaultMonth = useMemo(() => format(new Date(), 'yyyy-MM'), []);
  const [submitSuccess, setSubmitSuccess] = useState(null);
  const [secondsRemaining, setSecondsRemaining] = useState(SUCCESS_REDIRECT_SECONDS);
  const [formKey, setFormKey] = useState(0);
  const monthInputRef = useRef(null);

  useEffect(() => {
    if (!submitSuccess) return undefined;

    setSecondsRemaining(SUCCESS_REDIRECT_SECONDS);
    const tick = window.setInterval(() => {
      setSecondsRemaining(prev => (prev > 0 ? prev - 1 : 0));
    }, 1000);
    const timeout = window.setTimeout(() => {
      logInfo(LOG_CONTEXT, 'success view expired, remounting form', {
        timesheetId: submitSuccess.id,
      });
      setSubmitSuccess(null);
      setFormKey(k => k + 1);
    }, SUCCESS_REDIRECT_SECONDS * 1000);

    return () => {
      window.clearInterval(tick);
      window.clearTimeout(timeout);
    };
  }, [submitSuccess]);

  const initialValues = useMemo(
    () => ({
      first_name: '',
      last_name: '',
      worker_reference_id: '',
      month: defaultMonth,
      submitter_email: '',
      notes: '',
      daily_hours: buildDailyHoursForMonth(defaultMonth),
      expenses: [],
      attachments: [],
    }),
    [defaultMonth],
  );

  /**
   * Confirms month change when existing hour entries would be lost.
   *
   * @param {string} nextMonth - `YYYY-MM`
   * @param {Object} values - current Formik values
   * @param {Function} setFieldValue
   */
  const applyMonthChange = (nextMonth, values, setFieldValue) => {
    const prevRows = values.daily_hours || [];
    // sumDayHours includes variable_hours, so a variable-only day still warns.
    const hadMeaningful = prevRows.some(
      r => r._rowTouched || sumDayHours(r) > 0,
    );
    if (hadMeaningful && nextMonth !== values.month) {
      const ok = window.confirm(
        'שינוי החודש ימחק את כל השעות שהוזנו עבור החודש הקודם. להמשיך?',
      );
      if (!ok) return;
    }
    setFieldValue('month', nextMonth);
    setFieldValue('daily_hours', buildDailyHoursForMonth(nextMonth));
  };

  return (
    <div
      dir="rtl"
      lang="he"
      className="mx-auto p-3 p-md-4"
      style={{
        maxWidth: '960px',
        background: '#f9f9f9',
        borderRadius: '12px',
        boxShadow: '0 0 10px rgba(0,0,0,0.1)',
      }}
    >
      <div className="worker-ts-page-header">
        <img
          src={MinyanLogo}
          alt="גן המניין לוגו"
          className="worker-ts-page-logo"
        />
        <h2 style={{ textAlign: 'center', marginBottom: 0 }}>
          דיווח שעות חודשי - גן המניין
        </h2>
      </div>

      {submitSuccess ? (
        <Alert
          variant="success"
          className="worker-ts-success-panel mb-0"
          role="status"
          aria-live="polite"
        >
          <Alert.Heading className="worker-ts-success-panel__title">
            הדיווח נשלח בהצלחה
          </Alert.Heading>
          <p className="mb-2">
            מזהה אסמכתא: <strong>{submitSuccess.id}</strong>
          </p>
          <p className="mb-2">
            חודש הדיווח: <strong>{submitSuccess.month}</strong>
          </p>
          {submitSuccess.submitterEmail ? (
            <p className="mb-2">
              עותק הדוח (PDF ואקסל) יישלח לכתובת{' '}
              <strong>{submitSuccess.submitterEmail}</strong>
            </p>
          ) : null}
          <p className="mb-2 text-muted">
            הדיווח נקלט במערכת. ניתן לשמור את מזהה האסמכתא לצורך מעקב.
          </p>
          <p className="worker-ts-success-panel__countdown mb-0">
            טופס חדש ייפתח בעוד{' '}
            <strong>{secondsRemaining}</strong> שניות
          </p>
        </Alert>
      ) : (
      <Formik
        key={formKey}
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={async (values, { setSubmitting }) => {
          const sizeErrors = collectFileSizeErrors(
            values.expenses,
            values.daily_hours,
            values.attachments,
            MAX_UPLOAD_BYTES,
          );
          if (sizeErrors.length) {
            sizeErrors.forEach(msg =>
              showToast(msg, 'error', 'top-left', 5000),
            );
            setSubmitting(false);
            return;
          }

          const idTrimmed = values.worker_reference_id.trim();
          const emailTrimmed = (values.submitter_email || '').trim();
          const expensesPayload = (values.expenses || []).map(e => {
            const frontend_id = e.frontend_id || generateWorkerTimesheetFrontendId();
            const hasFile = e.receipt instanceof File;
            const row = {
              frontend_id,
              description: e.description.trim(),
              amount:
                typeof e.amount === 'string' ? parseFloat(e.amount) : Number(e.amount),
              has_receipt: hasFile,
            };
            if (hasFile) {
              row.receipt_filename = e.receipt.name;
              row.receipt_content_type =
                e.receipt.type || 'application/octet-stream';
              row.receipt_size = e.receipt.size;
            }
            return row;
          });

          const payload = {
            first_name: values.first_name.trim(),
            last_name: values.last_name.trim(),
            worker_reference_id: idTrimmed,
            month: values.month,
            notes: (values.notes || '').trim(),
            daily_hours: toPayloadDailyHours(values.daily_hours),
            expenses: expensesPayload,
          };
          if (emailTrimmed) {
            payload.submitter_email = emailTrimmed;
          }

          const attachmentRows = (values.attachments || [])
            .filter(a => a.file instanceof File)
            .map(a => ({
              ...a,
              frontend_id: a.frontend_id || generateWorkerTimesheetFrontendId(),
            }));
          if (attachmentRows.length) {
            payload.attachment_captions = attachmentRows.map(a =>
              (a.caption || '').trim(),
            );
          }

          const attachmentsWithIds = attachmentRows.map(a => ({
            file: a.file,
            frontend_id: a.frontend_id,
          }));

          const expenseReceiptsWithIds = (values.expenses || [])
            .map((e, i) => ({ e, frontend_id: expensesPayload[i].frontend_id }))
            .filter(x => x.e.receipt instanceof File)
            .map(x => ({
              file: x.e.receipt,
              frontend_id: x.frontend_id,
            }));

          const sickNotesWithIds = (values.daily_hours || [])
            .filter(r => r?.is_sick_day && r?.sick_note_file instanceof File)
            .map(r => ({
              file: r.sick_note_file,
              frontend_id:
                r.sick_note_frontend_id || generateWorkerTimesheetFrontendId(),
            }));

          logInfo(LOG_CONTEXT, 'submit timesheet', {
            month: payload.month,
            dailyRows: payload.daily_hours.length,
            transportHours: payload.daily_hours.reduce(
              (acc, row) => acc + (Number(row.transport_hours) || 0),
              0,
            ),
            variableHours: payload.daily_hours.reduce(
              (acc, row) => acc + (Number(row.variable_hours) || 0),
              0,
            ),
            expenseRows: payload.expenses.length,
            attachmentsWithIds: attachmentsWithIds.length,
            expenseReceiptsWithIds: expenseReceiptsWithIds.length,
            sickNotesWithIds: sickNotesWithIds.length,
            hasSubmitterEmail: Boolean(emailTrimmed),
          });

          const result = await submitWorkerTimesheet(payload, {
            attachmentsWithIds,
            expenseReceiptsWithIds,
            sickNotesWithIds,
          });

          if (result.success) {
            setSubmitSuccess({
              id: result.data.id,
              month: result.data.month,
              submitterEmail: emailTrimmed || null,
            });
            result.toast();
          } else {
            showToast(
              result.detail || 'השליחה נכשלה.',
              'error',
              'top-left',
              5000,
            );
          }
          setSubmitting(false);
        }}
      >
        {({ values, isSubmitting, setFieldValue }) => (
          <Form>
            <Row className="mb-3">
              <Col md={6} xs={12}>
                <BsForm.Label>
                  שם פרטי <RequiredAsteriskComponent />
                </BsForm.Label>
                <Field as={BsForm.Control} name="first_name" />
                <ErrorMessage
                  name="first_name"
                  component="div"
                  className="text-danger small"
                />
              </Col>
              <Col md={6} xs={12}>
                <BsForm.Label>
                  שם משפחה <RequiredAsteriskComponent />
                </BsForm.Label>
                <Field as={BsForm.Control} name="last_name" />
                <ErrorMessage
                  name="last_name"
                  component="div"
                  className="text-danger small"
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6} xs={12}>
                <BsForm.Label>
                  מספר תעודת זהות <RequiredAsteriskComponent />
                </BsForm.Label>
                <Field name="worker_reference_id">
                  {({ field }) => (
                    <BsForm.Control
                      {...field}
                      inputMode="numeric"
                      autoComplete="off"
                      placeholder="מספר תעודת זהות"
                    />
                  )}
                </Field>
                <ErrorMessage
                  name="worker_reference_id"
                  component="div"
                  className="text-danger small"
                />
              </Col>
              <Col md={6} xs={12}>
                <BsForm.Label>
                  חודש <RequiredAsteriskComponent />
                </BsForm.Label>
                <InputGroup className="worker-ts-month-input-group">
                  <Field name="month">
                    {({ field }) => (
                      <BsForm.Control
                        {...field}
                        ref={monthInputRef}
                        type="month"
                        className="worker-ts-month-input"
                        onChange={e => {
                          const next = e.target.value;
                          if (!next) return;
                          applyMonthChange(next, values, setFieldValue);
                        }}
                      />
                    )}
                  </Field>
                  <InputGroup.Text
                    className="worker-ts-month-input-addon"
                    role="button"
                    tabIndex={0}
                    aria-label="פתח בחירת חודש"
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        const el = monthInputRef.current;
                        if (!el) return;
                        if (typeof el.showPicker === 'function') {
                          el.showPicker().catch(() => {
                            el.click();
                          });
                        } else {
                          el.click();
                        }
                      }
                    }}
                    onClick={() => {
                      const el = monthInputRef.current;
                      if (!el) return;
                      if (typeof el.showPicker === 'function') {
                        el.showPicker().catch(() => {
                          el.click();
                        });
                      } else {
                        el.click();
                      }
                    }}
                  >
                    <Calendar3 aria-hidden="true" />
                  </InputGroup.Text>
                </InputGroup>
                <p className="d-md-none small text-muted mb-0 mt-1">
                  לחץ לבחירת חודש
                </p>
                <ErrorMessage
                  name="month"
                  component="div"
                  className="text-danger small"
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6} xs={12}>
                <BsForm.Label>אימייל לקבלת עותק הדוח</BsForm.Label>
                <Field name="submitter_email">
                  {({ field }) => (
                    <BsForm.Control
                      {...field}
                      type="email"
                      autoComplete="email"
                      placeholder="name@example.com"
                    />
                  )}
                </Field>
                <BsForm.Text className="text-muted">
                  לאחר שליחה מוצלחת, עותק של דוח ה-PDF וקובץ האקסל יישלחו לכתובת
                  זו
                </BsForm.Text>
                <ErrorMessage
                  name="submitter_email"
                  component="div"
                  className="text-danger small"
                />
              </Col>
            </Row>

            <Row className="mb-3">
              <Col>
                <BsForm.Label>הערות (אופציונלי)</BsForm.Label>
                <Field name="notes">
                  {({ field }) => (
                    <BsForm.Control {...field} as="textarea" rows={3} />
                  )}
                </Field>
              </Col>
            </Row>

            <h5 className="mb-3">שעות יומיות</h5>
            <DailyHoursListView />
            <ErrorMessage
              name="daily_hours"
              component="div"
              className="text-danger small mt-2"
            />

            <hr className="my-4" />
            <ExpensesSection />

            <hr className="my-4" />
            <AttachmentsSection maxFileBytes={MAX_UPLOAD_BYTES} />

            <div className="d-grid d-md-flex justify-content-md-end mt-4 gap-2">
              <Button
                type="submit"
                variant="primary"
                disabled={isSubmitting}
                className="py-3 py-md-2"
              >
                {isSubmitting ? (
                  <>
                    שולח...
                    <Spinner animation="border" size="sm" className="ms-2" />
                  </>
                ) : (
                  'שלח דיווח'
                )}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
      )}
    </div>
  );
};

export default WorkerTimesheetPage;
